/* eslint-disable no-console */
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Header } from "../../components";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { saveOportunidad } from "../../utils";
import { getClientesPorBroker, upsertCotizacionPorCodigo } from "../../utils/cotizacionesGuardadas";
import { buildSalesFromCotizacion } from "../../utils/formalizarCotizacion";
import { client as supabase } from "../../utils/supabase";
import styles from "./CotizacionesGeneradas.module.scss";

const MESES = [
  { value: "01", label: "ENE" },
  { value: "02", label: "FEB" },
  { value: "03", label: "MAR" },
  { value: "04", label: "ABR" },
  { value: "05", label: "MAY" },
  { value: "06", label: "JUN" },
  { value: "07", label: "JUL" },
  { value: "08", label: "AGO" },
  { value: "09", label: "SEP" },
  { value: "10", label: "OCT" },
  { value: "11", label: "NOV" },
  { value: "12", label: "DIC" },
];

const ANOS = [2025, 2026, 2027, 2028];

const formatFecha = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatInversion = (monto, monedaDolar) => {
  const num = Number(monto) || 0;
  const prefix = monedaDolar ? "US$" : "$";
  return `${prefix} ${num.toLocaleString("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const CotizacionesGeneradas = () => {
  const { pais, id, rol, agencia } = useAuthContext();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [ventasMap, setVentasMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
  const [modalVincular, setModalVincular] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [vinculando, setVinculando] = useState(false);
  const [tipoCotizacion, setTipoCotizacion] = useState("cliente");
  const [formVincular, setFormVincular] = useState({
    cliente_id: "",
    cliente_nombre: "",
    campana: "",
    canal: "B2C",
    estatus: "100%",
    mes_cierre: "",
    ano_cierre: "",
    mes_factura: "",
    ano_factura: "",
  });

  useEffect(() => {
    if (!pais) return;
    const fetchCotizaciones = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("cotizaciones")
          .select("*")
          .eq("pais", pais)
          .eq("eliminada", false)
          .gte("created_at", "2026-01-01")
          .order("created_at", { ascending: false });
        if (error) throw error;
        let cotData = data || [];

        // === SCOPING BROKER (app-level, fail-safe restrictivo) ===
        // El broker ve solo: las cotizaciones que creó él (usuario_id===id) O las vinculadas
        // a su cartera (cliente_id ∈ broker_id=agencia). Fail-safe: si la cartera no resuelve,
        // igual muestra lo propio (usuario_id), nunca todo. Demás roles: sin filtro.
        if (rol === ROLES.BROKER) {
          let carteraIds = new Set();
          try {
            const cartera = await getClientesPorBroker(pais, agencia);
            carteraIds = new Set((cartera || []).map((c) => Number(c.id)));
          } catch {
            carteraIds = new Set();
          }
          cotData = cotData.filter(
            (c) => String(c.usuario_id) === String(id) || (c.cliente_id != null && carteraIds.has(Number(c.cliente_id)))
          );
        }

        setCotizaciones(cotData);

        const codigos = cotData.map((c) => c.codigo_generado).filter(Boolean);
        if (codigos.length === 0) {
          setVentasMap(new Map());
          return;
        }
        const { data: ventasData, error: ventasError } = await supabase
          .from("sales")
          .select("code, estatus, cliente")
          .in("code", codigos)
          .eq("eliminada", false);
        if (ventasError) throw ventasError;
        const map = new Map();
        for (const v of ventasData || []) {
          map.set(v.code, v);
        }
        setVentasMap(map);
      } catch (err) {
        console.error("Error cargando cotizaciones generadas:", err);
        toast.error("Error al cargar cotizaciones");
      } finally {
        setLoading(false);
      }
    };
    fetchCotizaciones();
  }, [pais, id, rol, agencia]);

  useEffect(() => {
    if (!pais) return;
    const fetchClientes = async () => {
      try {
        // El broker solo puede vincular a clientes de su cartera (broker_id = su agencia).
        if (rol === ROLES.BROKER) {
          const cartera = await getClientesPorBroker(pais, agencia);
          setClientes(cartera || []);
          return;
        }
        const { data, error } = await supabase
          .from("clientes")
          .select("id, nombre, empresa")
          .eq("pais", pais)
          .eq("eliminada", false)
          .order("nombre", { ascending: true });
        if (error) throw error;
        setClientes(data || []);
      } catch (err) {
        console.error("Error cargando clientes:", err);
      }
    };
    fetchClientes();
  }, [pais, rol, agencia]);

  const handleVincular = async () => {
    if (!modalVincular) return;
    setVinculando(true);
    try {
      let ejecutivo = null;
      if (formVincular.cliente_id) {
        const { data: cli } = await supabase
          .from("clientes")
          .select("ejecutivo")
          .eq("id", Number(formVincular.cliente_id))
          .single();
        if (cli?.ejecutivo) ejecutivo = cli.ejecutivo;
      }
      const payload = buildSalesFromCotizacion(modalVincular, {
        cliente_id: formVincular.cliente_id,
        cliente_nombre: formVincular.cliente_nombre,
        campana: formVincular.campana,
        canal: formVincular.canal,
        estatus: formVincular.estatus,
        mes_cierre: formVincular.mes_cierre,
        ano_cierre: formVincular.ano_cierre,
        mes_factura: formVincular.mes_factura,
        ano_factura: formVincular.ano_factura,
      });
      if (ejecutivo) payload.ejecutivo = ejecutivo;
      await saveOportunidad(payload);
      await upsertCotizacionPorCodigo(modalVincular.codigo_generado, {
        estado: "ejecutada",
        cliente_id: formVincular.cliente_id,
        cliente_nombre: formVincular.cliente_nombre,
      });
      toast.success("Oportunidad vinculada correctamente");
      setCotizaciones((prev) =>
        prev.map((c) =>
          c.id === modalVincular.id ? { ...c, cliente_nombre: formVincular.cliente_nombre, estado: "ejecutada" } : c
        )
      );
      setModalVincular(null);
      setFormVincular({
        cliente_id: "",
        cliente_nombre: "",
        campana: "",
        canal: "B2C",
        estatus: "100%",
        mes_cierre: "",
        ano_cierre: "",
        mes_factura: "",
        ano_factura: "",
      });
    } catch (err) {
      console.error("Error vinculando cotización:", err);
      toast.error("Error al vincular");
    } finally {
      setVinculando(false);
    }
  };

  const cotizacionesFiltradas = useMemo(() => {
    let filtered = cotizaciones;

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      filtered = filtered.filter((c) => {
        const usuario = `${c.usuario_nombre || ""} ${c.usuario_apellido || ""}`.toLowerCase();
        const venta = ventasMap.get(c.codigo_generado);
        return (
          (c.codigo_generado || "").toLowerCase().includes(q) ||
          usuario.includes(q) ||
          (c.cliente_nombre || "").toLowerCase().includes(q) ||
          (venta?.estatus || "").toLowerCase().includes(q)
        );
      });
    }

    if (filtroEstado === "vinculada") {
      filtered = filtered.filter((c) => ventasMap.has(c.codigo_generado));
    } else if (filtroEstado === "sin_vincular") {
      filtered = filtered.filter((c) => !ventasMap.has(c.codigo_generado));
    }

    if (filtroFechaDesde) {
      const desde = new Date(filtroFechaDesde).getTime();
      filtered = filtered.filter((c) => c.created_at && new Date(c.created_at).getTime() >= desde);
    }

    if (filtroFechaHasta) {
      const hasta = new Date(filtroFechaHasta).getTime() + 24 * 60 * 60 * 1000 - 1;
      filtered = filtered.filter((c) => c.created_at && new Date(c.created_at).getTime() <= hasta);
    }

    return filtered;
  }, [cotizaciones, ventasMap, busqueda, filtroEstado, filtroFechaDesde, filtroFechaHasta]);

  const filtersContent = (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-end", width: "100%" }}>
      <input
        type="text"
        className="form-control"
        placeholder="Buscar por código, usuario o cliente..."
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        style={{ width: "150px", fontSize: "12px" }}
      />
      <select className={styles.filterSelect} value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
        <option value="">Todos</option>
        <option value="vinculada">Vinculada</option>
        <option value="sin_vincular">Sin vincular</option>
      </select>
      <span style={{ fontSize: "11px", color: "#666", whiteSpace: "nowrap" }}>Desde</span>
      <input
        type="date"
        className="form-control"
        value={filtroFechaDesde}
        onChange={(e) => setFiltroFechaDesde(e.target.value)}
        style={{ width: "140px", fontSize: "12px" }}
      />
      <span style={{ fontSize: "11px", color: "#666", whiteSpace: "nowrap" }}>Hasta</span>
      <input
        type="date"
        className="form-control"
        value={filtroFechaHasta}
        onChange={(e) => setFiltroFechaHasta(e.target.value)}
        style={{ width: "140px", fontSize: "12px" }}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <Header expandChildren>{filtersContent}</Header>
      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando...</div>
        ) : cotizacionesFiltradas.length === 0 ? (
          <div className={styles.empty}>Sin resultados</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.cotizacionesTable}>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Fecha</th>
                  <th>Creado por</th>
                  <th>Cliente / Acción</th>
                  <th>Estatus</th>
                  <th>Inversión</th>
                </tr>
              </thead>
              <tbody>
                {cotizacionesFiltradas.map((c) => {
                  const venta = ventasMap.get(c.codigo_generado);
                  const estatusKey = venta?.estatus ? venta.estatus.replace("%", "") : null;
                  return (
                    <tr key={c.id}>
                      <td style={{ textAlign: "left", fontWeight: 600 }}>{c.codigo_generado}</td>
                      <td style={{ textAlign: "center" }}>{formatFecha(c.created_at)}</td>
                      <td>{`${c.usuario_nombre || ""} ${c.usuario_apellido || ""}`.trim() || "—"}</td>
                      <td>
                        {c.cliente_nombre ? (
                          c.cliente_nombre
                        ) : (
                          <button
                            type="button"
                            onClick={() => setModalVincular(c)}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#007bff",
                              fontSize: "12px",
                              cursor: "pointer",
                              padding: "2px 6px",
                            }}
                          >
                            Vincular
                          </button>
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {venta?.estatus ? (
                          <span className={`${styles.estatusBadge} ${styles[`estatus_${estatusKey}`] || ""}`}>
                            {venta.estatus}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>{formatInversion(c.inversion_final_total, c.moneda_dolar)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={!!modalVincular} toggle={() => setModalVincular(null)} scrollable style={{ maxWidth: "600px" }}>
        <ModalHeader toggle={() => setModalVincular(null)}>Vincular oportunidad</ModalHeader>
        <ModalBody>
          <div className="mb-2">
            <label className="form-label fw-bold">Cliente Publicitario</label>
            <select
              className="form-select"
              value={formVincular.cliente_id}
              onChange={(e) => {
                const id = e.target.value;
                const cliente = clientes.find((cli) => String(cli.id) === String(id));
                setFormVincular((prev) => ({
                  ...prev,
                  cliente_id: id,
                  cliente_nombre: cliente?.nombre || cliente?.empresa || "",
                }));
              }}
            >
              <option value="">Selecciona un cliente</option>
              {clientes.map((cli) => (
                <option key={cli.id} value={cli.id}>
                  {cli.nombre || cli.empresa}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-2 d-flex align-items-center">
            <label className="form-label fw-bold mb-0" style={{ width: "50%" }}>
              ¿A quién va dirigida?
            </label>
            <div
              className="d-flex align-items-center"
              style={{
                width: "50%",
                background: "#e9ecef",
                borderRadius: "6px",
                padding: "2px",
                cursor: "pointer",
                fontSize: "0.78rem",
              }}
            >
              <span
                onClick={() => setTipoCotizacion("cliente")}
                style={{
                  flex: 1,
                  textAlign: "center",
                  padding: "4px 0",
                  borderRadius: "5px",
                  fontWeight: tipoCotizacion === "cliente" ? 600 : 400,
                  background: tipoCotizacion === "cliente" ? "#fff" : "transparent",
                  boxShadow: tipoCotizacion === "cliente" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                  transition: "all 0.15s",
                }}
              >
                Cliente Directo
              </span>
              <span
                onClick={() => setTipoCotizacion("agencia")}
                style={{
                  flex: 1,
                  textAlign: "center",
                  padding: "4px 0",
                  borderRadius: "5px",
                  fontWeight: tipoCotizacion === "agencia" ? 600 : 400,
                  background: tipoCotizacion === "agencia" ? "#fff" : "transparent",
                  boxShadow: tipoCotizacion === "agencia" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                  transition: "all 0.15s",
                }}
              >
                Agencia
              </span>
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label fw-bold">Campaña asociada (opcional)</label>
            <input
              type="text"
              className="form-control"
              maxLength={15}
              value={formVincular.campana}
              onChange={(e) =>
                setFormVincular((prev) => ({ ...prev, campana: e.target.value.toUpperCase().trimEnd() }))
              }
            />
          </div>

          <div className="mb-2 d-flex gap-3">
            <div style={{ width: "50%" }}>
              <label className="form-label fw-bold">Canal</label>
              <select
                className="form-select"
                value={formVincular.canal}
                onChange={(e) => setFormVincular((prev) => ({ ...prev, canal: e.target.value }))}
              >
                <option value="">Canal...</option>
                <option value="B2C">B2C</option>
                <option value="B2B">B2B</option>
                <option value="B2G">B2G</option>
              </select>
            </div>
            <div style={{ width: "50%" }}>
              <label className="form-label fw-bold">Status</label>
              <select
                className="form-select"
                value={formVincular.estatus}
                onChange={(e) => setFormVincular((prev) => ({ ...prev, estatus: e.target.value }))}
              >
                <option value="100%">100%</option>
                <option value="90%">90%</option>
                <option value="75%">75%</option>
                <option value="50%">50%</option>
              </select>
            </div>
          </div>

          <div className="mb-2 d-flex gap-3">
            <div style={{ width: "50%" }}>
              <label className="form-label fw-bold">Cierre</label>
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  value={formVincular.mes_cierre}
                  onChange={(e) => setFormVincular((prev) => ({ ...prev, mes_cierre: e.target.value }))}
                >
                  <option value="">Mes</option>
                  {MESES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  className="form-select"
                  value={formVincular.ano_cierre}
                  onChange={(e) => setFormVincular((prev) => ({ ...prev, ano_cierre: e.target.value }))}
                >
                  <option value="">Año</option>
                  {ANOS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ width: "50%" }}>
              <label className="form-label fw-bold">Factura</label>
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  value={formVincular.mes_factura}
                  onChange={(e) => setFormVincular((prev) => ({ ...prev, mes_factura: e.target.value }))}
                >
                  <option value="">Mes</option>
                  {MESES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  className="form-select"
                  value={formVincular.ano_factura}
                  onChange={(e) => setFormVincular((prev) => ({ ...prev, ano_factura: e.target.value }))}
                >
                  <option value="">Año</option>
                  {ANOS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <hr className="my-2" />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalVincular(null)} disabled={vinculando}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleVincular}
            disabled={
              vinculando ||
              !formVincular.cliente_id ||
              !formVincular.canal ||
              !formVincular.mes_cierre ||
              !formVincular.ano_cierre ||
              !formVincular.mes_factura ||
              !formVincular.ano_factura
            }
          >
            {vinculando ? "Vinculando..." : "Vincular"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CotizacionesGeneradas;
